import React, { useState, useEffect } from 'react';
import {
  X,
  Mail,
  Lock,
  User as UserIcon,
  LogIn,
  UserPlus,
  AlertCircle,
  CheckCircle2,
  ShieldCheck,
  Eye,
  EyeOff,
  ArrowLeft,
  Sparkles,
  Crown,
  BookOpen,
  HelpCircle,
  FileText,
  Upload,
  Copy,
  Check,
  Edit2,
  Save,
  Target,
  ArrowRight,
  SkipForward
} from 'lucide-react';
import {
  signUpWithEmail,
  signInWithEmail,
  signInWithGoogle,
  signOutUser,
  resetUserPassword,
  uploadFileToStorage,
  saveUserProfile,
  updateUserDisplayName,
  isUserAdmin,
  auth
} from '../lib/firebase';
import { UserProfile, AuthIntent, PraxisPaper } from '../types';
import { examCatalog, ExamCatalogEntry } from '../data/examCatalog';
import { PRAXIS_PAPERS } from '../data/praxisPapers';

// Same family/paper matching heuristic StudyGuides.tsx and ExamDetailPage.tsx
// use to resolve an Exam Catalog family entry to its concrete PraxisPapers --
// duplicated here (rather than imported) so a signup goal-exam pick resolves
// to the exact same "primary" paper a student would land on browsing that
// family elsewhere in the app.
function paperMatchesExamCode(paper: PraxisPaper, targetExamCodeRaw: string): boolean {
  const paperExamCode = paper.examCode ? paper.examCode.trim() : '';
  const targetExamCode = targetExamCodeRaw.trim();

  if (paperExamCode && paperExamCode.toLowerCase() === targetExamCode.toLowerCase()) {
    return true;
  }
  if (paperExamCode) {
    const lowerP = paperExamCode.toLowerCase();
    const lowerTarget = targetExamCode.toLowerCase();
    if (lowerP.startsWith(lowerTarget + '-') || lowerP === lowerTarget) {
      return true;
    }
  }
  if (paper.code) {
    const lowerCode = paper.code.trim().toLowerCase();
    const lowerTarget = targetExamCode.toLowerCase();
    if (lowerCode.startsWith(lowerTarget + '-') || lowerCode === lowerTarget) {
      return true;
    }
  }
  if (targetExamCode === 'Praxis-Core' && paper.category === 'core' && (!paperExamCode || paperExamCode.startsWith('Praxis'))) return true;
  if (targetExamCode === 'Praxis-Elementary' && (paper.category === 'elementary' || paper.code === '5001')) return true;
  if (targetExamCode === 'Praxis-Subject' && (paper.category === 'subject' || paper.category === 'elementary')) return true;
  if (targetExamCode === 'Praxis-PLT' && paper.category === 'plt') return true;

  return false;
}

// Resolves an Exam Catalog family entry to the id that should be stored as
// UserProfile.targetPaperId: the first PraxisPaper that matches any of the
// family's examCodes, in catalog declaration order. Several catalog
// families (NYSTCE, TExES, FTCE, ILTS, PECT, GACE) currently have no
// authored PraxisPaper content at all -- for those we fall back to the
// family's own catalog id so the field is always a defined, non-empty
// string (Firestore's setDoc rejects `undefined` field values outright,
// and a bare fallback still round-trips through findFamilyForTargetPaperId
// below so the choice is never silently lost).
function resolveTargetPaperIdForFamily(entry: ExamCatalogEntry, papers: PraxisPaper[]): string {
  for (const code of entry.examCodes) {
    const match = papers.find((p) => paperMatchesExamCode(p, code));
    if (match) return match.id;
  }
  return entry.id;
}

// Reverse lookup for the revisable selector in the signed-in profile view
// and for the post-signup step's "already picked" highlight: given a saved
// targetPaperId, find which Exam Catalog family it belongs to, whether that
// value is a real PraxisPaper id or one of the bare family-id fallbacks
// resolveTargetPaperIdForFamily can produce above.
function findFamilyForTargetPaperId(targetPaperId: string | undefined, papers: PraxisPaper[]): ExamCatalogEntry | null {
  if (!targetPaperId) return null;
  const paper = papers.find((p) => p.id === targetPaperId);
  if (paper) {
    const viaPaper = examCatalog.find((entry) => entry.examCodes.some((code) => paperMatchesExamCode(paper, code)));
    if (viaPaper) return viaPaper;
  }
  return examCatalog.find((entry) => entry.id === targetPaperId) || null;
}

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  userProfile: UserProfile;
  onProfileUpdate: (updated: UserProfile) => void;
  initialMode?: 'signin' | 'signup';
  intent?: AuthIntent | null;
  onAuthSuccess?: (profile: UserProfile, intent?: AuthIntent | null) => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  userProfile,
  onProfileUpdate,
  initialMode = 'signup',
  intent = null,
  onAuthSuccess
}) => {
  const [mode, setMode] = useState<'signin' | 'signup'>(initialMode);
  const [isResetView, setIsResetView] = useState<boolean>(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);

  // Post-signup goal-exam step. Skippable, never blocks signup itself --
  // shown only once the account already exists, right before handing
  // control back to onAuthSuccess/onClose.
  const [showGoalExamStep, setShowGoalExamStep] = useState(false);
  const [pendingProfile, setPendingProfile] = useState<UserProfile | null>(null);
  const [savingGoalExam, setSavingGoalExam] = useState(false);

  // Revisable goal-exam selector, shown in the signed-in profile view.
  const [editingGoalExam, setEditingGoalExam] = useState(false);
  const [savingProfileGoalExam, setSavingProfileGoalExam] = useState(false);

  // Feedback states
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Legal Modal sub-view
  const [legalView, setLegalView] = useState<'terms' | 'privacy' | null>(null);

  // Authenticated profile management states
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [copiedUid, setCopiedUid] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [savingName, setSavingName] = useState(false);

  const currentUser = auth.currentUser;
  const effectiveUid = (userProfile?.uid && userProfile.uid !== 'guest_user_1')
    ? userProfile.uid
    : (currentUser?.uid || '');

  const effectiveEmail = (userProfile?.email && userProfile.email !== 'future.teacher@praxisprep.edu')
    ? userProfile.email
    : (currentUser?.email || (userProfile?.email !== 'future.teacher@praxisprep.edu' ? userProfile?.email : ''));

  const effectiveDisplayName = (userProfile?.displayName && userProfile.displayName !== 'Future Educator' && userProfile.displayName !== 'PRAXIS Educator')
    ? userProfile.displayName
    : (currentUser?.displayName || (effectiveEmail ? effectiveEmail.split('@')[0] : (userProfile?.displayName || 'PRAXIS Educator')));

  const isGuest = !effectiveUid && !currentUser?.uid;

  const initialLetter = (typeof effectiveDisplayName === 'string' && effectiveDisplayName.trim().length > 0
    ? effectiveDisplayName.trim().charAt(0)
    : (effectiveEmail ? effectiveEmail.charAt(0) : 'P')).toUpperCase();

  const isAdmin = isUserAdmin(userProfile) || (effectiveEmail && effectiveEmail.toLowerCase() === 'delightpeter12@gmail.com');

  const currentGoalFamily = findFamilyForTargetPaperId(userProfile?.targetPaperId, PRAXIS_PAPERS);

  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
      setIsResetView(false);
      setErrorMsg(null);
      setSuccessMsg(null);
      setResetSent(false);
      setLegalView(null);
      setShowGoalExamStep(false);
      setPendingProfile(null);
      if (email) setResetEmail(email);
    }
  }, [isOpen, initialMode]);

  useEffect(() => {
    setEditedName(effectiveDisplayName);
  }, [effectiveDisplayName, isOpen]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        if (legalView) {
          setLegalView(null);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, legalView, onClose]);

  if (!isOpen) return null;

  // Password strength calculation
  const getPasswordStrength = (pass: string) => {
    if (!pass) return { score: 0, label: 'None', color: 'bg-slate-200', text: 'text-slate-400' };
    let score = 0;
    if (pass.length >= 8) score += 1;
    if (/[A-Z]/.test(pass) && /[a-z]/.test(pass)) score += 1;
    if (/[0-9]/.test(pass)) score += 1;
    if (/[^A-Za-z0-9]/.test(pass)) score += 1;

    switch (score) {
      case 1:
        return { score: 1, label: 'Weak', color: 'bg-rose-500', text: 'text-rose-600' };
      case 2:
        return { score: 2, label: 'Fair', color: 'bg-amber-500', text: 'text-amber-600' };
      case 3:
        return { score: 3, label: 'Good', color: 'bg-teal-500', text: 'text-teal-700' };
      case 4:
        return { score: 4, label: 'Strong', color: 'bg-emerald-500', text: 'text-emerald-600' };
      default:
        return { score: 0, label: 'Too short', color: 'bg-slate-200', text: 'text-slate-400' };
    }
  };

  const passwordStrength = getPasswordStrength(password);

  // Dynamic Contextual Header Formulation
  const getContextualHeader = () => {
    const examLabel = intent?.exam?.shortLabel || intent?.exam?.state || intent?.contextTitle;

    if (intent?.actionType === 'exam' && examLabel) {
      return mode === 'signup'
        ? `Create your free account to start practicing ${examLabel}`
        : `Log in to start practicing ${examLabel}`;
    }

    if (intent?.actionType === 'guides') {
      return mode === 'signup'
        ? 'Create your free account to continue to Study Guides'
        : 'Log in to continue to Study Guides';
    }

    if (intent?.actionType === 'community') {
      return mode === 'signup'
        ? 'Create your free account to continue to Community'
        : 'Log in to continue to Community';
    }

    if (intent?.actionType === 'tutoring') {
      return mode === 'signup'
        ? 'Create your free account to book 1-on-1 tutoring'
        : 'Log in to book 1-on-1 tutoring';
    }

    if (intent?.actionType === 'review') {
      return mode === 'signup'
        ? 'Create your free account to access spaced review'
        : 'Log in to access your spaced review queue';
    }

    if (intent?.actionType === 'preview') {
      return mode === 'signup'
        ? 'Create your free account to start your free preview'
        : 'Log in to continue your free preview';
    }

    if (intent?.actionType === 'pricing') {
      return mode === 'signup'
        ? 'Create your free account to unlock full access'
        : 'Log in to unlock full access';
    }

    if (intent?.actionType === 'resume-test') {
      return mode === 'signup'
        ? 'Create your free account to keep going'
        : 'Log in to keep going';
    }

    if (intent?.actionType === 'general' || !intent) {
      return mode === 'signup'
        ? 'Create your free account'
        : 'Log in to your account';
    }

    return mode === 'signup'
      ? 'Create your free account to continue'
      : 'Log in to continue';
  };

  const getSubheader = () => {
    if (intent?.actionType === 'resume-test') {
      return mode === 'signup'
        ? 'Get 5 more questions on this paper, plus 5 free on every other paper. No card needed.'
        : 'Your free questions carry over, and you get 5 more on this paper.';
    }
    if (mode === 'signup') {
      return 'Instant access to 5 free practice questions per exam paper. No credit card required.';
    }
    return 'Enter your credentials to access your diagnostic tests and progress.';
  };

  const handleCopyUid = () => {
    if (!effectiveUid) return;
    navigator.clipboard.writeText(effectiveUid);
    setCopiedUid(true);
    setTimeout(() => setCopiedUid(false), 2000);
  };

  const handleSaveDisplayName = async () => {
    if (!editedName.trim()) {
      setErrorMsg('Display name cannot be blank.');
      return;
    }
    setSavingName(true);
    setErrorMsg(null);
    try {
      const updated = await updateUserDisplayName(editedName.trim());
      onProfileUpdate(updated);
      setIsEditingName(false);
      setSuccessMsg('Display name updated successfully!');
      setTimeout(() => setSuccessMsg(null), 2500);
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to update display name.');
    } finally {
      setSavingName(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingAvatar(true);
    setErrorMsg(null);

    try {
      const targetUid = effectiveUid || 'guest_user_1';
      const res = await uploadFileToStorage(file, `avatars/${targetUid}`);
      const updatedProfile = { ...userProfile, uid: targetUid, avatarUrl: res.url };
      await saveUserProfile(updatedProfile);
      onProfileUpdate(updatedProfile);
      setSuccessMsg('Profile picture updated!');
      setTimeout(() => setSuccessMsg(null), 2000);
    } catch (err: any) {
      setErrorMsg('Failed to upload avatar: ' + err.message);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOutUser();
      const guestProfile: UserProfile = {
        uid: 'guest_user_1',
        displayName: 'Future Educator',
        email: 'future.teacher@praxisprep.edu',
        tier: 'free',
        unlockedPaperIds: [],
        targetState: 'ETS_NATIONAL',
        dailyGoalMinutes: 30,
        streakDays: 4,
        totalStudyMinutes: 210,
        createdAt: new Date().toISOString()
      };
      onProfileUpdate(guestProfile);
      setSuccessMsg('Signed out successfully.');
      setTimeout(() => onClose(), 800);
    } catch (err: any) {
      setErrorMsg('Failed to sign out: ' + err.message);
    }
  };

  // Hands control back to the parent (App.tsx) once auth -- and, if shown,
  // the goal-exam step -- is fully done.
  const finishAuthFlow = (profile: UserProfile) => {
    if (onAuthSuccess) {
      onAuthSuccess(profile, intent);
    }
    onClose();
  };

  const handleSelectGoalExam = async (entry: ExamCatalogEntry) => {
    if (!pendingProfile) return;
    setSavingGoalExam(true);
    setErrorMsg(null);
    try {
      const targetPaperId = resolveTargetPaperIdForFamily(entry, PRAXIS_PAPERS);
      const updated: UserProfile = { ...pendingProfile, targetPaperId };
      await saveUserProfile(updated);
      onProfileUpdate(updated);
      finishAuthFlow(updated);
    } catch (err: any) {
      console.error('Goal exam save error:', err);
      setErrorMsg(err?.message || 'Failed to save your goal exam. You can set it later from your profile.');
    } finally {
      setSavingGoalExam(false);
    }
  };

  const handleSkipGoalExam = () => {
    if (pendingProfile) {
      finishAuthFlow(pendingProfile);
    }
  };

  const handleChangeGoalExam = async (entry: ExamCatalogEntry) => {
    setSavingProfileGoalExam(true);
    setErrorMsg(null);
    try {
      const targetPaperId = resolveTargetPaperIdForFamily(entry, PRAXIS_PAPERS);
      const updated: UserProfile = { ...userProfile, targetPaperId, targetExamFamily: entry.id };
      await saveUserProfile(updated);
      onProfileUpdate(updated);
      setEditingGoalExam(false);
      setSuccessMsg('Goal exam updated!');
      setTimeout(() => setSuccessMsg(null), 2000);
    } catch (err: any) {
      console.error('Goal exam update error:', err);
      setErrorMsg(err?.message || 'Failed to update your goal exam.');
    } finally {
      setSavingProfileGoalExam(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setSuccessMsg(null);
    setLoading(true);

    try {
      if (mode === 'signup') {
        if (!displayName.trim() || !email.trim() || !password.trim()) {
          throw new Error('Please fill in all required fields.');
        }
        if (password.length < 8) {
          throw new Error('Password must be at least 8 characters long.');
        }
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match.');
        }
        if (!agreedToTerms) {
          throw new Error('Please accept the Terms of Service and Privacy Policy to continue.');
        }

        // signUpWithEmail also asks Firebase to email the verification link.
        // The welcome email is no longer sent here: it now goes out once,
        // from the server, after the student finishes the onboarding
        // screener (App.tsx -> OnboardingFlow), so it can name their exam.
        const profile = await signUpWithEmail(email.trim(), password, displayName.trim());

        onProfileUpdate(profile);
        setSuccessMsg('Account created! Check your email for a verification link.');

        // Hand straight over to App.tsx's onboarding screen (verify email,
        // then exam / date / daily goal), which replaces the old in-modal
        // goal-exam step for new accounts.
        setTimeout(() => {
          finishAuthFlow(profile);
        }, 800);
      } else {
        if (!email.trim() || !password.trim()) {
          throw new Error('Please enter your email and password.');
        }
        const profile = await signInWithEmail(email.trim(), password);
        onProfileUpdate(profile);
        setSuccessMsg('Signed in successfully! Redirecting...');

        setTimeout(() => {
          finishAuthFlow(profile);
        }, 800);
      }
    } catch (err: any) {
      console.error('Auth Error:', err);
      let msg = err.message || 'Authentication failed.';
      if (err.code === 'auth/email-already-in-use') {
        msg = 'This email address is already registered. Please switch to the Log In tab.';
      } else if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
        msg = 'Invalid email or password. Please verify your details.';
      } else if (err.code === 'auth/user-not-found') {
        msg = 'No account found with this email. Please create a free account.';
      } else if (err.code === 'auth/weak-password') {
        msg = 'Password is too weak. Please use at least 8 characters.';
      } else if (err.code === 'auth/invalid-email') {
        msg = 'Please enter a valid email address.';
      }
      setErrorMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setErrorMsg(null);
    setSuccessMsg(null);
    setLoading(true);

    try {
      const profile = await signInWithGoogle();
      onProfileUpdate(profile);
      setSuccessMsg('Signed in with Google! Redirecting...');

      // Google already verifies the email address, so there is no
      // verification step. If this account has never picked an exam,
      // App.tsx's onboarding screener opens right after this closes.
      setTimeout(() => {
        finishAuthFlow(profile);
      }, 800);
    } catch (err: any) {
      console.error('Google Auth Error:', err);
      setErrorMsg(err.message || 'Google Sign-In was cancelled or failed.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail.trim()) {
      setErrorMsg('Please enter your registered email address.');
      return;
    }
    setErrorMsg(null);
    setSuccessMsg(null);
    setLoading(true);

    try {
      await resetUserPassword(resetEmail.trim());
      setResetSent(true);
      setSuccessMsg(`Password reset instructions sent to ${resetEmail.trim()}`);
    } catch (err: any) {
      console.error('Reset password error:', err);
      let msg = err.message || 'Failed to send password reset email.';
      if (err.code === 'auth/user-not-found') {
        msg = 'No account registered with this email address.';
      } else if (err.code === 'auth/invalid-email') {
        msg = 'Please enter a valid email address.';
      }
      setErrorMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full p-5 sm:p-6 space-y-4 shadow-2xl border border-slate-200 relative overflow-hidden my-auto text-left"
        onClick={(e) => e.stopPropagation()}
      >

        {/* Top Accent Strip */}
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-[#0E9594] z-10" />

        {/* Modal Header */}
        <div className="flex items-start justify-between gap-3 -mx-5 sm:-mx-6 -mt-5 sm:-mt-6 px-5 sm:px-6 pt-6 pb-4 bg-gradient-to-br from-[#0f1b4d] to-[#1b2f7a]">
          <div className="flex items-start gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center shrink-0 mt-0.5">
              <ShieldCheck className="w-5 h-5 text-teal-300" />
            </div>
            <div>
              <h3 className="font-display font-extrabold text-white text-base sm:text-lg leading-snug">
                {showGoalExamStep
                  ? 'What exam are you preparing for?'
                  : !isGuest
                  ? 'Educator Profile & Account'
                  : isResetView
                  ? 'Reset Your Password'
                  : getContextualHeader()
                }
              </h3>
              <p className="text-xs text-indigo-100/80 mt-0.5 leading-relaxed">
                {showGoalExamStep
                  ? "We'll pin it to the top of your dashboard. You can change this anytime."
                  : !isGuest
                  ? 'Manage your credential profile & security'
                  : isResetView
                  ? 'We will send a secure reset link to your email'
                  : getSubheader()
                }
              </p>
            </div>
          </div>

          <button
            onClick={showGoalExamStep ? handleSkipGoalExam : onClose}
            className="p-1.5 rounded-lg text-indigo-200 hover:text-white hover:bg-white/10 transition cursor-pointer shrink-0"
            title="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Feedback Alerts */}
        {errorMsg && (
          <div className="bg-rose-50 border border-rose-200 text-rose-800 p-3 rounded-xl text-xs flex items-start gap-2.5">
            <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
            <div className="flex-1 font-semibold">{errorMsg}</div>
          </div>
        )}

        {successMsg && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-3 rounded-xl text-xs flex items-center gap-2.5">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <div className="font-semibold">{successMsg}</div>
          </div>
        )}

        {/* Post-Signup Goal Exam Step (Skippable) */}
        {showGoalExamStep ? (
          <div className="space-y-4 py-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-80 overflow-y-auto pr-0.5">
              {examCatalog.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  disabled={savingGoalExam}
                  onClick={() => handleSelectGoalExam(entry)}
                  className="text-left p-3 rounded-xl border border-slate-200 bg-slate-50/50 hover:bg-white hover:border-[#0E9594] hover:shadow-xs transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex flex-col gap-1 group"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-extrabold text-xs text-slate-900 group-hover:text-[#0E9594] transition-colors">
                      {entry.state}
                    </span>
                    <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-[#0E9594] group-hover:translate-x-0.5 transition" />
                  </div>
                  <p className="text-[11px] text-slate-500 font-medium line-clamp-2">
                    {entry.shortLabel}
                  </p>
                </button>
              ))}
            </div>

            {savingGoalExam && (
              <p className="text-center text-[11px] font-semibold text-slate-400">Saving your goal exam...</p>
            )}

            <button
              type="button"
              onClick={handleSkipGoalExam}
              disabled={savingGoalExam}
              className="w-full text-center text-xs font-bold text-slate-500 hover:text-slate-700 py-2 flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
            >
              <SkipForward className="w-3.5 h-3.5" />
              Skip for now
            </button>
          </div>
        ) : /* Signed In User Profile View (If already authenticated) */
        !isGuest ? (
          <div className="space-y-4 py-1">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-4">
                <div className="relative shrink-0">
                  {userProfile.avatarUrl ? (
                    <img
                      src={userProfile.avatarUrl}
                      alt="Avatar"
                      className="w-14 h-14 rounded-full object-cover border-2 border-[#0E9594] shadow-xs"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-[#0f1b4d] text-white font-extrabold text-xl flex items-center justify-center border-2 border-teal-200 shadow-xs">
                      {initialLetter}
                    </div>
                  )}

                  <label className="absolute -bottom-1 -right-1 bg-white border border-slate-300 p-1 rounded-full text-slate-600 hover:text-[#0E9594] cursor-pointer shadow-xs">
                    <Upload className="w-3 h-3" />
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleAvatarUpload}
                      disabled={uploadingAvatar}
                    />
                  </label>
                </div>

                <div className="flex-1 min-w-0 space-y-1">
                  {isEditingName ? (
                    <div className="flex items-center gap-1.5">
                      <input
                        type="text"
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        className="bg-white border border-teal-400 rounded-lg px-2 py-1 text-xs font-bold text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#0E9594] w-full"
                        placeholder="Enter full name"
                        autoFocus
                      />
                      <button
                        onClick={handleSaveDisplayName}
                        disabled={savingName}
                        className="p-1 rounded-md bg-[#0f1b4d] hover:bg-[#1b2f7a] text-white text-xs shrink-0 cursor-pointer"
                        title="Save name"
                      >
                        <Save className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => { setIsEditingName(false); setEditedName(effectiveDisplayName); }}
                        className="p-1 rounded-md bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs shrink-0 cursor-pointer"
                        title="Cancel"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="font-extrabold text-slate-900 text-sm flex items-center gap-2">
                      <span className="truncate">{effectiveDisplayName}</span>
                      <button
                        onClick={() => setIsEditingName(true)}
                        className="text-slate-400 hover:text-[#0E9594] transition p-0.5 cursor-pointer"
                        title="Edit display name"
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                      {isAdmin && (
                        <span className="px-1.5 py-0.2 bg-amber-400 text-slate-950 text-[9px] font-black rounded shrink-0">ADMIN</span>
                      )}
                    </div>
                  )}
                  <div className="text-xs text-slate-600 truncate font-medium">{effectiveEmail}</div>
                  <div className="flex items-center gap-1.5 pt-0.5">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-teal-50 text-teal-800 border border-teal-200 uppercase tracking-wider">
                      <Crown className="w-3 h-3" />
                      Tier: {userProfile.tier || 'free'}
                    </span>
                  </div>
                </div>
              </div>

              {/* UID Verification Box */}
              <div className="pt-2 border-t border-slate-200/80 flex items-center justify-between gap-2 bg-white/80 rounded-lg p-2 border border-slate-200 text-xs">
                <div className="flex items-center gap-1.5 min-w-0 font-mono text-[11px] text-slate-600">
                  <span className="font-bold text-slate-400 uppercase tracking-wider text-[10px]">UID:</span>
                  <span className="truncate font-semibold text-slate-800 select-all" title={effectiveUid}>
                    {effectiveUid || 'N/A'}
                  </span>
                </div>
                {effectiveUid && (
                  <button
                    onClick={handleCopyUid}
                    className="flex items-center gap-1 px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-[10px] font-bold shrink-0 transition cursor-pointer"
                    title="Copy UID to clipboard"
                  >
                    {copiedUid ? (
                      <>
                        <Check className="w-3 h-3 text-emerald-600" />
                        <span className="text-emerald-600">Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3 text-slate-500" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Revisable Goal Exam Selector */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-teal-50 text-[#0E9594] flex items-center justify-center shrink-0">
                    <Target className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-bold text-slate-900">Goal Exam</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {currentGoalFamily ? currentGoalFamily.shortLabel : 'Not set yet'}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setEditingGoalExam((v) => !v)}
                  className="text-[11px] font-bold text-[#0E9594] hover:text-[#0b7a79] hover:underline cursor-pointer shrink-0"
                >
                  {editingGoalExam ? 'Cancel' : currentGoalFamily ? 'Change' : 'Set Goal'}
                </button>
              </div>

              {editingGoalExam && (
                <div className="space-y-2 pt-1 border-t border-slate-200/80">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-0.5 pt-2">
                    {examCatalog.map((entry) => (
                      <button
                        key={entry.id}
                        type="button"
                        disabled={savingProfileGoalExam}
                        onClick={() => handleChangeGoalExam(entry)}
                        className={`text-left p-2.5 rounded-lg border transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex flex-col gap-0.5 ${
                          currentGoalFamily?.id === entry.id
                            ? 'border-[#0E9594] bg-teal-50'
                            : 'border-slate-200 bg-white hover:border-teal-300'
                        }`}
                      >
                        <span className="font-extrabold text-[11px] text-slate-900">{entry.state}</span>
                        <span className="text-[10px] text-slate-500 line-clamp-1">{entry.shortLabel}</span>
                      </button>
                    ))}
                  </div>
                  {savingProfileGoalExam && (
                    <p className="text-center text-[11px] font-semibold text-slate-400">Saving...</p>
                  )}
                </div>
              )}
            </div>

            <div className="pt-1">
              <button
                onClick={handleSignOut}
                className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs py-2.5 px-4 rounded-xl transition flex items-center justify-center gap-2 border border-slate-200 cursor-pointer"
              >
                Sign Out of Account
              </button>
            </div>
          </div>
        ) : isResetView ? (
          /* ========================================================================= */
          /* FORGOT PASSWORD RESET VIEW */
          /* ========================================================================= */
          <div className="space-y-4 pt-1">
            {resetSent ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-3 text-center">
                <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <div className="space-y-1">
                  <h4 className="font-bold text-slate-900 text-sm">Check Your Inbox</h4>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    We've dispatched password reset instructions to <strong>{resetEmail}</strong>. Click the link in the email to set a new password.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setIsResetView(false);
                    setResetSent(false);
                    setMode('signin');
                  }}
                  className="w-full mt-2 bg-[#0f1b4d] hover:bg-[#1b2f7a] text-white font-bold text-xs py-2 px-4 rounded-xl transition cursor-pointer"
                >
                  Return to Log In
                </button>
              </div>
            ) : (
              <form onSubmit={handlePasswordReset} className="space-y-3.5">
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Your Registered Email
                  </label>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="email"
                      required
                      placeholder="johndoe@email.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-3 py-2.5 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0E9594] transition"
                      autoFocus
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !resetEmail.trim()}
                  className="w-full bg-[#0f1b4d] hover:bg-[#1b2f7a] text-white font-bold text-xs py-2.5 px-4 rounded-xl transition shadow-xs flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="animate-pulse">Sending Reset Link...</span>
                  ) : (
                    <span>Send Password Reset Email</span>
                  )}
                </button>

                <div className="text-center pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setIsResetView(false);
                      setErrorMsg(null);
                    }}
                    className="text-xs font-bold text-slate-500 hover:text-[#0E9594] inline-flex items-center gap-1 cursor-pointer"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to Log In
                  </button>
                </div>
              </form>
            )}
          </div>
        ) : (
          /* ========================================================================= */
          /* TWO-TAB AUTH MODAL (SIGN UP / LOG IN) */
          /* ========================================================================= */
          <div className="space-y-4">

            {/* Tab Selector Switcher */}
            <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200">
              <button
                type="button"
                onClick={() => {
                  setMode('signup');
                  setErrorMsg(null);
                  setSuccessMsg(null);
                }}
                className={`flex-1 py-2 text-xs font-extrabold rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                  mode === 'signup'
                    ? 'bg-[#0f1b4d] text-white shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <UserPlus className="w-3.5 h-3.5" />
                <span>Sign Up</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setMode('signin');
                  setErrorMsg(null);
                  setSuccessMsg(null);
                }}
                className={`flex-1 py-2 text-xs font-extrabold rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                  mode === 'signin'
                    ? 'bg-[#0f1b4d] text-white shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <LogIn className="w-3.5 h-3.5" />
                <span>Log In</span>
              </button>
            </div>

            {/* Google 1-Click Auth Button */}
            <button
              type="button"
              onClick={handleGoogleAuth}
              disabled={loading}
              className="w-full bg-white border border-slate-300 hover:bg-slate-50 hover:border-slate-400 text-slate-700 font-bold text-xs py-2.5 px-4 rounded-2xl transition flex items-center justify-center gap-2.5 shadow-2xs cursor-pointer disabled:opacity-50"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                />
              </svg>
              <span>{mode === 'signup' ? 'Sign Up with Google' : 'Continue with Google'}</span>
            </button>

            {/* Divider */}
            <div className="relative flex items-center justify-center">
              <div className="border-t border-slate-200 w-full" />
              <span className="bg-white px-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-wider shrink-0 absolute">
                Or with Email
              </span>
            </div>

            {/* Email & Password Form */}
            <form onSubmit={handleEmailAuth} className="space-y-3 pt-0.5">

              {/* Full Name Field (Sign Up Only) */}
              {mode === 'signup' && (
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Full Name
                  </label>
                  <div className="relative">
                    <UserIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      required
                      placeholder="e.g. John Doe"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-3 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0E9594] transition"
                    />
                  </div>
                </div>
              )}

              {/* Email Field */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="email"
                    required
                    placeholder="johndoe@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-3 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0E9594] transition"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-bold text-slate-700">
                    Password
                  </label>

                  {/* Forgot Password Link (Log In Mode Only) */}
                  {mode === 'signin' && (
                    <button
                      type="button"
                      onClick={() => {
                        setResetEmail(email);
                        setIsResetView(true);
                        setErrorMsg(null);
                      }}
                      className="text-[11px] font-bold text-[#0E9594] hover:text-[#0b7a79] hover:underline cursor-pointer"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>

                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    placeholder={mode === 'signup' ? 'At least 8 characters' : '••••••••'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-10 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0E9594] transition"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition cursor-pointer p-0.5"
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                {/* Real-time Password Strength Indicator (Sign Up Mode) */}
                {mode === 'signup' && (
                  <div className="mt-2 space-y-1 bg-slate-50 border border-slate-200 rounded-xl p-2.5">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-slate-500 font-medium">Password strength:</span>
                      <span className={`font-bold ${passwordStrength.text}`}>
                        {passwordStrength.label}
                      </span>
                    </div>

                    <div className="grid grid-cols-4 gap-1 h-1.5 w-full">
                      <div className={`rounded-full transition-all ${passwordStrength.score >= 1 ? passwordStrength.color : 'bg-slate-200'}`} />
                      <div className={`rounded-full transition-all ${passwordStrength.score >= 2 ? passwordStrength.color : 'bg-slate-200'}`} />
                      <div className={`rounded-full transition-all ${passwordStrength.score >= 3 ? passwordStrength.color : 'bg-slate-200'}`} />
                      <div className={`rounded-full transition-all ${passwordStrength.score >= 4 ? passwordStrength.color : 'bg-slate-200'}`} />
                    </div>

                    <p className="text-[10px] text-slate-500 pt-0.5">
                      Must be at least 8 characters. Mix upper/lower case & numbers for best security.
                    </p>
                  </div>
                )}
              </div>

              {/* Confirm Password Field (Sign Up Only) */}
              {mode === 'signup' && (
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      required
                      placeholder="Re-enter your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`w-full bg-white border rounded-xl pl-9 pr-10 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 transition ${
                        confirmPassword
                          ? (password === confirmPassword ? 'border-emerald-400 focus:ring-emerald-500' : 'border-rose-400 focus:ring-rose-500')
                          : 'border-slate-200 focus:ring-[#0E9594]'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition cursor-pointer p-0.5"
                      title={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {confirmPassword && (
                    <p className={`text-[10px] pt-1 font-semibold ${password === confirmPassword ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {password === confirmPassword ? 'Passwords match' : 'Passwords do not match'}
                    </p>
                  )}
                </div>
              )}

              {/* Terms of Service & Privacy Policy Checkbox (Sign Up Mode) */}
              {mode === 'signup' && (
                <div className="pt-1">
                  <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="mt-0.5 rounded border-slate-300 text-[#0E9594] focus:ring-[#0E9594] cursor-pointer"
                    />
                    <span className="text-[11px] leading-relaxed">
                      I agree to the{' '}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setLegalView('terms');
                        }}
                        className="text-[#0E9594] font-bold hover:underline"
                      >
                        Terms of Service
                      </button>{' '}
                      and{' '}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setLegalView('privacy');
                        }}
                        className="text-[#0E9594] font-bold hover:underline"
                      >
                        Privacy Policy
                      </button>
                      . Free tier access applies immediately.
                    </span>
                  </label>
                </div>
              )}

              {/* Submit Action Button */}
              <button
                type="submit"
                disabled={
                  loading ||
                  !email.trim() ||
                  !password.trim() ||
                  (mode === 'signup' && (
                    !displayName.trim() ||
                    password.length < 8 ||
                    password !== confirmPassword ||
                    !agreedToTerms
                  ))
                }
                className="w-full bg-[#0f1b4d] hover:bg-[#1b2f7a] text-white font-black text-xs sm:text-sm py-3 px-4 rounded-2xl transition shadow-md flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed mt-3"
              >
                {loading ? (
                  <span className="animate-pulse">
                    {mode === 'signup' ? 'Creating Free Account...' : 'Signing in...'}
                  </span>
                ) : mode === 'signup' ? (
                  <>
                    <UserPlus className="w-4 h-4" />
                    <span>Create Free Account</span>
                  </>
                ) : (
                  <>
                    <LogIn className="w-4 h-4" />
                    <span>Log In</span>
                  </>
                )}
              </button>
            </form>

            {/* Bottom Switcher Helper */}
            <div className="text-center pt-2 border-t border-slate-200">
              {mode === 'signin' ? (
                <p className="text-xs text-slate-500">
                  New to PrePraxis Prep?{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setMode('signup');
                      setErrorMsg(null);
                      setSuccessMsg(null);
                    }}
                    className="text-[#0E9594] font-bold hover:underline cursor-pointer"
                  >
                    Create Free Account
                  </button>
                </p>
              ) : (
                <p className="text-xs text-slate-500">
                  Already have an educator account?{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setMode('signin');
                      setErrorMsg(null);
                      setSuccessMsg(null);
                    }}
                    className="text-[#0E9594] font-bold hover:underline cursor-pointer"
                  >
                    Log In Here
                  </button>
                </p>
              )}
            </div>

          </div>
        )}

        {/* ========================================================================= */}
        {/* INLINE LEGAL MODAL OVERLAY (Terms & Privacy without navigating away) */}
        {/* ========================================================================= */}
        {legalView && (
          <div className="absolute inset-0 bg-white z-20 p-5 rounded-2xl flex flex-col justify-between overflow-y-auto">
            <div className="space-y-3">
              <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                <h4 className="font-extrabold text-slate-900 text-sm flex items-center gap-1.5">
                  <FileText className="w-4 h-4 text-[#0E9594]" />
                  {legalView === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
                </h4>
                <button
                  onClick={() => setLegalView(null)}
                  className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="text-xs text-slate-600 space-y-2.5 max-h-72 overflow-y-auto pr-1">
                {legalView === 'terms' ? (
                  <>
                    <p className="font-semibold text-slate-800">1. Free Preview & Subscription Terms</p>
                    <p>
                      PrePraxis Prep provides teacher candidates with a free preview tier consisting of 5 questions per exam paper upon account registration. Upgraded subscriptions grant unlimited mock simulations and domain reviews.
                    </p>
                    <p className="font-semibold text-slate-800">2. Independent Prep Provider</p>
                    <p>
                      PrePraxis Prep is an independent prep provider and is not affiliated with or endorsed by CTC, ETS, TEA, Pearson, or state licensing boards. All trademarks belong to their respective owners.
                    </p>
                    <p className="font-semibold text-slate-800">3. Single-Device Subscription Usage</p>
                    <p>
                      Active paying subscriptions enforce single active session usage to maintain secure testing integrity across our candidate community.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-semibold text-slate-800">1. Information We Collect</p>
                    <p>
                      We collect your email address, display name, and diagnostic test attempt metrics to calculate your spaced repetition review queues and score estimates.
                    </p>
                    <p className="font-semibold text-slate-800">2. Data Security & Storage</p>
                    <p>
                      Your account records and question mastery data are protected by strict cloud database security rules and encrypted transport. We never sell your personal data.
                    </p>
                    <p className="font-semibold text-slate-800">3. Communications</p>
                    <p>
                      We send study reminders and score summaries based on your configured notification preferences.
                    </p>
                  </>
                )}
              </div>
            </div>

            <div className="pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => {
                  setAgreedToTerms(true);
                  setLegalView(null);
                }}
                className="w-full bg-[#0f1b4d] hover:bg-[#1b2f7a] text-white font-bold text-xs py-2 rounded-xl transition cursor-pointer"
              >
                I Understand & Agree
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};